/**
 * Uyarılar sayfası (#229) — sunucunun `alertsData` yükünü (sözleşme: Workspace
 * docs/api-contracts/dashboard-alerts.md) kart listesine çevirir.
 *
 * `drawerTitle` değerleri dashboard'daki uyarı listesiyle BİREBİR aynıdır:
 * AlertDrawer kategoriyi (Serviste / Plasiyerde / Tüketim / Kritik) ve
 * sipariş listesini başlıktan tanır.
 */

export const ALERT_SEVERITIES = {
  danger: { label: "Kritik", order: 0 },
  warning: { label: "Uyarı", order: 1 },
  info: { label: "Bilgi", order: 2 },
};

export const ALERT_GROUPS = [
  { key: "devices", label: "Cihaz Uyarıları" },
  { key: "firms", label: "Firma Uyarıları" },
  { key: "orders", label: "Sipariş Takibi" },
];

// Sipariş listesi ürün başına satırdır — kartta firma sayısı gösterilir.
const countDistinctFirms = (rows) =>
  new Set(rows.map((r) => String(r?._id ?? r?.firmId ?? "")).filter(Boolean)).size;

/** @returns {Array<{id,group,iconKey,severity,dataType,label,description,drawerTitle}>} */
const categoryDefs = (longStayDays) => [
  {
    id: "criticalDevices",
    group: "devices",
    source: ["devices", "critical"],
    iconKey: "criticalDevice",
    severity: "danger",
    dataType: "device",
    label: "Kritik seviyeli cihazlar",
    description: "Kotası kritik seviyeye inmiş cihazlar",
    drawerTitle: "Kritik Seviyeli Cihazlar",
  },
  {
    id: "unConnectedDevices",
    group: "devices",
    source: ["devices", "unConnected"],
    iconKey: "signalLost",
    severity: "warning",
    dataType: "device",
    label: "Bağlanmayan cihazlar",
    description: "3 gündür sunucuya bağlanmamış",
    drawerTitle: "3 Gündür Bağlanmayan Cihazlar",
  },
  {
    id: "unUsedDevices",
    group: "devices",
    source: ["devices", "unUsed"],
    iconKey: "idleCup",
    severity: "info",
    dataType: "device",
    label: "Kota tüketmeyen cihazlar",
    description: "3 gündür hiç kota tüketilmemiş",
    drawerTitle: "3 Gündür Tüketim Yapmayan Cihazlar",
  },
  {
    id: "longServiceDevices",
    group: "devices",
    source: ["devices", "longService"],
    iconKey: "serviceWrench",
    severity: "warning",
    dataType: "device",
    label: "Serviste bekleyen cihazlar",
    description: `${longStayDays} günden uzun süredir serviste`,
    drawerTitle: `${longStayDays}+ Gündür Serviste`,
  },
  {
    id: "longPlaymakerDevices",
    group: "devices",
    source: ["devices", "longPlaymaker"],
    iconKey: "playmakerTruck",
    severity: "warning",
    dataType: "device",
    label: "Plasiyerde bekleyen cihazlar",
    description: `${longStayDays} günden uzun süredir plasiyerde`,
    drawerTitle: `${longStayDays}+ Gündür Plasiyerde`,
  },
  {
    id: "criticalQuotaFirms",
    group: "firms",
    source: ["firms", "criticalQuota"],
    iconKey: "criticalQuota",
    severity: "danger",
    dataType: "firm",
    label: "Kritik kotalı firmalar",
    description: "Kotası tükenmek üzere olan firmalar",
    drawerTitle: "Kritik Kotalı Firmalar",
  },
  {
    id: "lowPerformanceFirms",
    group: "firms",
    source: ["firms", "lowPerformance"],
    iconKey: "lowPerformance",
    severity: "info",
    dataType: "firm",
    label: "Düşük performanslı firmalar",
    description: "Kota tüketimi beklenenin altında",
    drawerTitle: "Düşük Performanslı Firmalar",
  },
  {
    id: "notOrderedFirms",
    group: "orders",
    source: ["orders", "notOrdered"],
    iconKey: "noOrder",
    severity: "warning",
    dataType: "firm",
    label: "Sipariş vermeyen firmalar",
    description: "Sipariş sıklığına göre gecikmiş firmalar",
    drawerTitle: "Sipariş Vermeyen Firmalar",
    countFn: countDistinctFirms,
  },
];

/**
 * @param {object|null} payload - alertsData
 * @returns {Array<object>} yalnız izinli (dilimi null olmayan) kategoriler;
 *   her biri { ...def, items, count }
 */
export function buildAlertCategories(payload) {
  if (!payload) return [];
  const longStayDays = payload.longStayDays ?? 15;
  return categoryDefs(longStayDays)
    .filter(({ source: [slice] }) => payload[slice] != null)
    .map(({ source: [slice, key], countFn, ...def }) => {
      const items = Array.isArray(payload[slice][key]) ? payload[slice][key] : [];
      return { ...def, items, count: countFn ? countFn(items) : items.length };
    });
}

/** Önem derecesine göre toplamlar — { total, danger, warning, info } */
export function summarizeAlerts(categories) {
  const sum = { total: 0, danger: 0, warning: 0, info: 0 };
  categories.forEach((c) => {
    sum.total += c.count;
    sum[c.severity] += c.count;
  });
  return sum;
}

const SLICES = ["devices", "firms", "orders"];

/**
 * Kademeli gönderim (#238): sunucu her dilim hazır oldukça yeni bir yük iter;
 * yeniden hesaplanan (pending) dilim `null` gelir. Eldeki eski dilim varsa
 * korunur — sayfa canlı yenilemede iskelete dönüp titremez. İzni kalkmış
 * dilim (access false) korunmaz.
 */
export function mergeAlertsPayload(prev, next) {
  if (!next) return prev;
  if (!prev) return next;
  const merged = { ...next };
  SLICES.forEach((slice) => {
    if (
      next[slice] == null &&
      next.pending?.[slice] &&
      next.access?.[slice] &&
      prev[slice] != null
    ) {
      merged[slice] = prev[slice];
    }
  });
  return merged;
}

/** Henüz hiç verisi gelmemiş, hesaplanmakta olan grup anahtarları */
export function pendingAlertGroups(payload) {
  if (!payload) return [];
  return SLICES.filter((slice) => payload.pending?.[slice] && payload[slice] == null);
}
